import { useState, type FormEvent } from "react";
import { useNavigate, useSearchParams, Link as RouterLink } from "react-router-dom";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import Alert from "@mui/material/Alert";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Snackbar from "@mui/material/Snackbar";
import { useTheme } from "@mui/material/styles";
import { apiClient, ApiError } from "../lib/apiClient";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../i18n/I18nProvider";
import { errorMessage } from "../i18n/apiErrors";
import LanguageSwitcher from "../components/LanguageSwitcher";
import PageWrap from "../components/PageWrap";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Extrahiert den Fehler_Code aus einem `ApiError`-Objekt — gleiches Muster wie
 * `extractCode` in `i18n/apiErrors.ts`: der Code wird AUSSCHLIESSLICH aus
 * `err.body.error.code` gelesen, nicht aus dem Backend_Fehlertext.
 */
function extractErrorCode(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  const body = err.body;
  if (typeof body !== "object" || body === null || !("error" in body)) {
    return null;
  }
  const errorField = (body as { error: unknown }).error;
  if (
    typeof errorField !== "object" ||
    errorField === null ||
    !("code" in errorField)
  ) {
    return null;
  }
  const code = (errorField as { code: unknown }).code;
  return typeof code === "string" && code.length > 0 ? code : null;
}

/**
 * `true`, wenn der Fehler ein `ApiError` mit Status 403 und dem Fehler_Code
 * `EMAIL_NOT_VERIFIED` ist — der harte Login-Block bei unbestätigter E-Mail.
 */
function isEmailNotVerified(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.status === 403 &&
    extractErrorCode(err) === "EMAIL_NOT_VERIFIED"
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type Mode = "login" | "register";

export default function LandingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const { t } = useI18n();
  const theme = useTheme();

  // Start-Modus aus dem Query-Param ableiten (Deep-Link der Willkommensseite,
  // `/?mode=register`); sonst wie bisher mit "login" starten. Unbekannte Werte
  // fallen sicher auf "login" zurück.
  const [mode, setMode] = useState<Mode>(
    searchParams.get("mode") === "register" ? "register" : "login",
  );
  const [isLoading, setIsLoading] = useState(false);
  // Halte das Fehler-Objekt (statt eines fixen Strings), damit der Fehlertext
  // bei Sprachwechsel über errorMessage(t, error) neu aufgelöst wird (Req 12.6).
  const [error, setError] = useState<unknown>(null);
  // Erfolgs-Hinweis nach der Registrierung (kein Auto-Login mehr): der Nutzer
  // verbleibt auf der Seite und wird gebeten, seine E-Mail zu bestätigen.
  const [notice, setNotice] = useState<string | null>(null);
  // Login-403-Block (E-Mail nicht bestätigt): zeigt einen lokalisierten Hinweis
  // plus „E-Mail erneut senden“-Button statt der generischen Fehler-Alert.
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [isResending, setIsResending] = useState(false);
  // Generische Resend-Bestätigung (Anti-Enumeration): immer dieselbe Meldung.
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Form fields
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  function switchMode(next: Mode) {
    setMode(next);
    setError(null);
    setNotice(null);
    setEmailNotVerified(false);
    setName("");
    setEmail("");
    setPassword("");
  }

  async function handleResend() {
    setIsResending(true);
    try {
      // Anti-Enumeration: der Endpunkt antwortet immer 200; wir zeigen danach
      // eine generische Snackbar, unabhängig davon, ob die Adresse existiert.
      await apiClient.post("/api/auth/resend-verification", { email });
    } catch {
      // Bewusst verschluckt: die Rückmeldung ist generisch und darf keine
      // Rückschlüsse auf den Kontostatus zulassen.
    } finally {
      setIsResending(false);
      setSnackbarOpen(true);
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setEmailNotVerified(false);
    setIsLoading(true);

    try {
      if (mode === "login") {
        await login(email, password);
        navigate("/groups");
      } else {
        // Registrierung ohne Auto-Login: nach erfolgreichem Anlegen verbleibt
        // der Nutzer auf der Seite und bestätigt zuerst seine E-Mail (Req 3.1, 7.2).
        await apiClient.post("/api/auth/register", { name, email, password });
        setNotice(t("landing.register.checkEmail"));
      }
    } catch (err) {
      if (isEmailNotVerified(err)) {
        // Harter Login-Block bei unbestätigter E-Mail (Req 7.2): statt der
        // generischen Fehler-Alert einen Hinweis + Resend-Angebot zeigen.
        setEmailNotVerified(true);
      } else {
        // Fehler-Objekt halten; der Text wird beim Rendern via errorMessage(t, err)
        // aufgelöst, sodass er bei Sprachwechsel neu ermittelt wird (Req 12.6).
        setError(err);
      }
    } finally {
      setIsLoading(false);
    }
  }

  return (
    // Zentrierte Einzel-Karte (Prototyp Login): kein AppHead, Sprachumschalter
    // sitzt oben rechts INNERHALB der Karte.
    <PageWrap center>
      <Card elevation={0} sx={{ width: "100%", maxWidth: 400, p: 3 }}>
        {/* Sprach_Umschalter oben rechts (anonymer Pfad, nur Sprach_Cache) */}
        <Stack direction="row" justifyContent="flex-end" mb={1}>
          <LanguageSwitcher />
        </Stack>

        {/* Logo statt Text-Überschrift (Anmelden/Registrieren). Es gibt eine
            helle und eine dunkle Variante in frontend/public/; die Auswahl folgt
            dem Theme-Modus (prefers-color-scheme). alt nutzt den i18n-App-Namen. */}
        <Box
          component="img"
          src={
            theme.palette.mode === "dark"
              ? "/se-logo-dunkel.png"
              : "/se-logo-hell.png"
          }
          alt={t("common.appName")}
          sx={{
            display: "block",
            mx: "auto",
            mb: 2.5,
            width: "100%",
            maxWidth: 220,
            height: "auto",
          }}
        />

        {/* Mode tabs */}
        <ToggleButtonGroup
          exclusive
          fullWidth
          value={mode}
          onChange={(_e, next: Mode | null) => next && switchMode(next)}
          sx={{ mb: 2.5 }}
          aria-label={t("common.appName")}
        >
          <ToggleButton value="login" sx={{ minHeight: 44 }}>
            {t("landing.tab.login")}
          </ToggleButton>
          <ToggleButton value="register" sx={{ minHeight: 44 }}>
            {t("landing.tab.register")}
          </ToggleButton>
        </ToggleButtonGroup>

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Stack gap={2}>
            {/* Name field — register only */}
            {mode === "register" && (
              <TextField
                id="name"
                type="text"
                label={t("landing.field.name")}
                autoComplete="name"
                required
                fullWidth
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isLoading}
              />
            )}

            {/* Email */}
            <TextField
              id="email"
              type="email"
              label={t("landing.field.email")}
              autoComplete={mode === "login" ? "email" : "username"}
              required
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
            />

            {/* Password */}
            <TextField
              id="password"
              type="password"
              label={t("landing.field.password")}
              autoComplete={mode === "login" ? "current-password" : "new-password"}
              required
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={isLoading}
              aria-busy={isLoading}
              sx={{ minHeight: 48 }}
            >
              {isLoading
                ? mode === "login"
                  ? t("landing.submit.signingIn")
                  : t("landing.submit.registering")
                : mode === "login"
                  ? t("landing.submit.signIn")
                  : t("landing.submit.createAccount")}
            </Button>

            {/* Erfolgs-Hinweis nach Registrierung (kein Auto-Login) */}
            {notice !== null && (
              <Alert severity="success" role="status">
                {notice}
              </Alert>
            )}

            {/* Login-403: E-Mail nicht bestätigt — Hinweis + Resend-Angebot
                (Req 7.2) statt der generischen Fehler-Alert. */}
            {emailNotVerified && (
              <Alert severity="warning" role="alert">
                <Stack gap={1.5} alignItems="flex-start">
                  <span>{t("landing.login.emailNotVerified")}</span>
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={handleResend}
                    disabled={isResending}
                    aria-busy={isResending}
                    sx={{ minHeight: 44 }}
                  >
                    {t("landing.login.resend")}
                  </Button>
                </Stack>
              </Alert>
            )}

            {/* Inline error message — bei Sprachwechsel neu aufgelöst (Req 12.6) */}
            {error !== null && (
              <Alert severity="error" role="alert">
                {errorMessage(t, error)}
              </Alert>
            )}
          </Stack>
        </Box>

        {/* Einstieg für neue Nutzer: die Willkommensseite erklärt das Konzept. */}
        <Link
          component={RouterLink}
          to="/welcome"
          variant="body2"
          sx={{ display: "block", textAlign: "center", mt: 2.5 }}
        >
          {t("landing.link.whatIs")}
        </Link>
      </Card>

      {/* Generische Resend-Rückmeldung (Anti-Enumeration): immer dieselbe
          Meldung, unabhängig vom Kontostatus. */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={t("verifyEmail.resendSnackbar")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </PageWrap>
  );
}
